import { keepPreviousData, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { core, downloadBlob } from "../../lib/api";
import type {
  AttentionCounts,
  Batch,
  BatchActionStatus,
  BatchCallbacksResponse,
  BatchDetail,
  LiveCallsResponse,
  LiveTranscriptDetail,
} from "./types";

/* Keep the board close enough to live state for running and recently completed batches. */
export const useActiveBatches = (enabled = true, ownerKey = "current", workflowId?: string) =>
  useQuery({
    queryKey: ["batches", "active", ownerKey, workflowId || "all"],
    queryFn: () => {
      const qs = new URLSearchParams({ scope: "active", limit: "500" });
      if (workflowId) qs.set("workflow_id", workflowId);
      return core<Batch[]>(`/api/v2/schedules?${qs}`);
    },
    enabled,
    staleTime: 5_000,
    // A 503 means the backend is temporarily unavailable (enforce-mode auth outage
    // included): back off instead of hammering it every 5s. Anything else keeps the
    // live board fresh. 401s never reach here -- the request wrapper redirects.
    refetchInterval: (query) =>
      (query.state.error as { status?: number } | null)?.status === 503 ? 60_000 : 5_000,
    refetchIntervalInBackground: false,
  });

export const useBatchLiveCalls = (scheduleId: string, enabled: boolean) =>
  useQuery({
    queryKey: ["batches", scheduleId, "live-calls"],
    queryFn: () =>
      core<LiveCallsResponse>(`/api/v1/schedules/${encodeURIComponent(scheduleId)}/live-transcripts`),
    enabled: enabled && Boolean(scheduleId),
    staleTime: 2_000,
    refetchInterval: enabled ? (query) =>
      (query.state.error as { status?: number } | null)?.status === 503 ? 60_000 : 3_000 : false,
    refetchIntervalInBackground: false,
    retry: false,
  });

/** The batch's contact list. Only fetched while the details panel is open --
 *  a batch can carry thousands of contacts, so this is never part of the board. */
export const useBatchContacts = (scheduleId: string, enabled: boolean) =>
  useQuery({
    queryKey: ["batches", scheduleId, "contacts"],
    queryFn: () => core<BatchDetail>(`/api/v1/schedules/${encodeURIComponent(scheduleId)}`),
    enabled: enabled && Boolean(scheduleId),
    staleTime: 30_000,
  });

export const useBatchCallbacks = (scheduleId: string, enabled: boolean) =>
  useQuery({
    queryKey: ["batches", scheduleId, "callbacks"],
    queryFn: () =>
      core<BatchCallbacksResponse>(
        `/api/v1/schedules/${encodeURIComponent(scheduleId)}/callbacks?pending=true`,
      ),
    enabled: enabled && Boolean(scheduleId),
    staleTime: 15_000,
  });

export const useLiveTranscript = (scheduleId: string, callId: string, enabled: boolean) =>
  useQuery({
    queryKey: ["batches", scheduleId, "live-transcript", callId],
    queryFn: () =>
      core<LiveTranscriptDetail>(
        `/api/v1/schedules/${encodeURIComponent(scheduleId)}/live-transcripts/${encodeURIComponent(callId)}`,
      ),
    enabled: enabled && Boolean(scheduleId) && Boolean(callId),
    staleTime: 1_000,
    refetchInterval: enabled ? 2_000 : false,
    refetchIntervalInBackground: false,
    retry: false,
  });

export const useAttention = () =>
  useQuery({
    queryKey: ["batches", "attention"],
    queryFn: () => core<AttentionCounts>("/api/v2/schedules?scope=attention&count_only=true"),
    refetchInterval: 60_000, refetchIntervalInBackground: false,
  });

export interface ArchiveParams { from: string; to: string; status: string; q: string; cursor?: string; workflow_id?: string }
export interface ArchivePage { items: Batch[]; next_cursor?: string }

export const useArchive = (p: ArchiveParams, enabled: boolean) =>
  useQuery({
    queryKey: ["batches", "archive", p],
    queryFn: () => {
      const qs = new URLSearchParams({ scope: "archive", from: p.from, to: p.to, limit: "50" });
      if (p.status !== "all") qs.set("status", p.status);
      const qClean = (p.q || "").trim();
      // ID-like queries (sched_xxx or hex fragment) are filtered client-side
      // in ArchiveDrawer so they work even before the backend ships ID search.
      const isIdLike = /^sched_/i.test(qClean) || /^[a-f0-9]{4,}$/i.test(qClean.replace(/^sched_/i, ""));
      if (qClean && !isIdLike) qs.set("q", qClean);
      if (p.cursor) qs.set("cursor", p.cursor);
      if (p.workflow_id) qs.set("workflow_id", p.workflow_id);
      return core<ArchivePage>(`/api/v2/schedules?${qs}`);
    },
    enabled, staleTime: 30_000, placeholderData: keepPreviousData,
  });

/* B2: no drag-between-columns; explicit transitions only. BR-04-05: 409 on illegal. */
export function useBatchAction() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, patch }: { id: string; patch: { status?: BatchActionStatus; concurrency?: number } }) =>
      core<Batch>(`/api/v2/schedules/${id}`, { method: "PUT", body: JSON.stringify(patch) }),
    onSettled: () => { qc.invalidateQueries({ queryKey: ["batches", "active"] }); qc.invalidateQueries({ queryKey: ["batches", "attention"] }); },
  });
}

export function useRetryFromArchive() {
  const qc = useQueryClient();
  return useMutation({
    /* B4: idempotency_key makes double-clicks safe */
    mutationFn: (b: Batch) => core<Batch>("/api/v2/schedules", {
      method: "POST",
      body: JSON.stringify({ source_schedule_id: b.schedule_id, idempotency_key: `retry-${b.schedule_id}-${Date.now()}` }),
    }),
    onSettled: () => qc.invalidateQueries({ queryKey: ["batches", "active"] }),
  });
}

export const exportResults = (b: Batch) =>
  downloadBlob(`/api/v2/schedules/${b.schedule_id}/results/export.csv`, `${b.name.replace(/\s+/g, "_")}_results.csv`);
